import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { ReactComponent as GrowthIcon } from '../assets/growth.svg';
import { ReactComponent as DecreaseIcon } from '../assets/decrees.svg';
import SvgGauge from '../components/SvgGauge'
import '../styles/FTSOSummary.css';

import { useStatus } from '../context/StatusContext';
import { useDataUpdates } from '../hooks/useDataUpdates';
import LoadingAnimation from '../components/loadingAnimation';
import 'react-circular-progressbar/dist/styles.css';
import { getApiBase } from '../utils/apiBase';
import { useDataCache } from '../hooks/useDataCache';
import { cacheManager } from '../hooks/GlobalCacheManager';

// Moved getNeedleColor outside the component for stability
const getNeedleColor = (hitRate) => {
  const normalizedRate = Math.min(Math.max(hitRate, 0), 100);
  const colors = {
    0: "#DC3545",    // red
    50: "#FFA500",   // amber
    100: "#008000"   // green
  };

  let lowerBound = 0;
  let upperBound = 50;
  
  if (normalizedRate > 50) {
    lowerBound = 50;
    upperBound = 100;
  }

  const ratio = (upperBound - lowerBound === 0) ? 0 : (normalizedRate - lowerBound) / (upperBound - lowerBound);
  const startColor = colors[lowerBound].match(/\w\w/g).map(x => parseInt(x, 16));
  const endColor = colors[upperBound].match(/\w\w/g).map(x => parseInt(x, 16));
  const r = Math.round(startColor[0] + (endColor[0] - startColor[0]) * ratio);
  const g = Math.round(startColor[1] + (endColor[1] - startColor[1]) * ratio);
  const b = Math.round(startColor[2] + (endColor[2] - startColor[2]) * ratio);
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

function FTSOSummary({ displayOrder, onScrollerReady, onScrollerDestroyed }) {
  const [chartData, setChartData] = useState({});
  const [votePowerData, setVotePowerData] = useState({});
  const [voterRegistration, setVoterRegistration] = useState({});
  const [generalInfoData, setGeneralInfoData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { updatePageStatus } = useStatus();
  const [entityNetworkList, setEntityNetworkList] = useState([]);
  const internalScrollerRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const prevChartDataRef = useRef({});
  const [latestSummaryVR, setLatestSummaryVR] = useState({}); // entity -> network -> vr

  // Persisted last-good values to avoid first-load placeholders
  const LS_CHART = 'hm_ftso_summary_chart';
  const LS_GENERAL = 'hm_ftso_summary_general';
  const LS_REG = 'hm_ftso_summary_reg';
  const LS_VP = 'hm_ftso_summary_vp';

  useLayoutEffect(() => {
    if (!loading && internalScrollerRef.current) {
      if (typeof onScrollerReady === 'function') {
        onScrollerReady(internalScrollerRef.current);
      }
    } 
    return () => {
      if (typeof onScrollerDestroyed === 'function') {
        onScrollerDestroyed();
      }
    };
  }, [loading, onScrollerReady, onScrollerDestroyed]);

  useLayoutEffect(() => {
    const scroller = internalScrollerRef.current;
    if (scroller) {
      scroller.scrollLeft = 0;
    }
  }, [displayOrder]);

  const API = getApiBase();
  const FTSO_PERFORMANCE_ENDPOINT = `${API}/ftso_performance_dashboard.json`;
  const SUBMISSION_SUMMARY_ENDPOINT = `${API}/submission_summary.json`;
  const SUBMIT2_PRICES_ENDPOINT = `${API}/submit2prices.json`;
  const FEED_CONFIG_ENDPOINT = `${API}/feed_config.json`;
  const { getCachedData } = useDataCache();

  // Smooth gauge wrapper to avoid needle bouncing on rapid updates
  const SmoothGauge = React.useMemo(() => {
    return function SmoothGaugeInner({ id, value }) {
      return (
        <SvgGauge id={id} value={typeof value === 'number' ? value : 0} width={240} height={140} arcWidth={0.18} colors={["#DC3545", "#FFA500", "#008000"]} />
      );
    };
  }, []);

  // Hydrate from persisted last-good values for instant first paint
  useEffect(() => {
    try {
      const chartRaw = localStorage.getItem(LS_CHART);
      const generalRaw = localStorage.getItem(LS_GENERAL);
      const regRaw = localStorage.getItem(LS_REG);
      const vpRaw = localStorage.getItem(LS_VP);
      let hydrated = false;
      if (chartRaw) {
        const parsed = JSON.parse(chartRaw);
        if (parsed && typeof parsed === 'object') {
          setChartData(parsed);
          prevChartDataRef.current = parsed;
          hydrated = true;
        }
      }
      if (generalRaw) {
        const parsed = JSON.parse(generalRaw);
        if (parsed && typeof parsed === 'object') {
          setGeneralInfoData(parsed);
          hydrated = true;
        }
      }
      if (regRaw) {
        const parsed = JSON.parse(regRaw);
        if (parsed && typeof parsed === 'object') {
          setVoterRegistration(parsed);
          hydrated = true;
        }
      }
      if (vpRaw) {
        const parsed = JSON.parse(vpRaw);
        if (parsed && typeof parsed === 'object') {
          setVotePowerData(parsed);
          hydrated = true;
        }
      }
      if (hydrated) {
        setLoading(false);
      }
    } catch {}
  }, []);

  const fetchData = useCallback(async () => {
    if (isInitialLoadRef.current) {
      setLoading(true);
    }
    setError(null);

    console.log('FTSOSummary: consume cache if available, else fetch dashboard');
    // Try cache first (SSE prefetch)
    try {
      const cachedDash = getCachedData('/ftso_performance_dashboard.json');
      if (cachedDash) {
        const dashboardData = cachedDash;
        const newChartData = {};
        const newVotePowerData = {};
        const newVoterRegistration = {};
        const newGeneralInfo = {};

        Object.keys(dashboardData).forEach(entity => {
          if (entity === "metadata") return;
          newChartData[entity] = {};
          newVotePowerData[entity] = {};
          newVoterRegistration[entity] = {};
          newGeneralInfo[entity] = {};
          Object.keys(dashboardData[entity]).forEach(network => {
            newChartData[entity][network] = dashboardData[entity][network]?.detailed_metrics?.hit_rates || {};
            newVotePowerData[entity][network] = dashboardData[entity][network]?.detailed_metrics?.vote_power || {};
            newVoterRegistration[entity][network] = dashboardData[entity][network]?.detailed_metrics?.voter_registration || {};
            newGeneralInfo[entity][network] = dashboardData[entity][network]?.general || {};
          });
        });

        // Build latest summary VR map from cached summary
        try {
          if (cachedDash) {
            const sumCache = getCachedData('/submission_summary.json');
            if (sumCache && typeof sumCache === 'object') {
              const vrMap = {};
              Object.keys(sumCache || {}).forEach(entity => {
                if (entity === 'metadata') return;
                vrMap[entity] = vrMap[entity] || {};
                Object.keys(sumCache[entity] || {}).forEach(network => {
                  const v = Number(sumCache[entity][network]?.latest_voting_round || 0);
                  vrMap[entity][network] = v;
                });
              });
              setLatestSummaryVR(vrMap);
            }
          }
        } catch {}

        // Fallback on cache path as well: if hit_rates missing/empty, compute from artifacts.
        // If cached artifacts are missing, fetch them once to avoid showing 0.00% due to empty cache.
        const isEmptyOrZero = (hrObj) => {
          if (!hrObj || Object.keys(hrObj).length === 0) return true;
          const values = ['15min','2hr','6hr'].map(tf => Number(hrObj?.[tf]?.hitRate));
          // treat NaN or all zeros as empty/invalid for fallback
          const validNums = values.filter(v => Number.isFinite(v));
          return validNums.length === 0 || validNums.every(v => v === 0);
        };
        const needsFallback = Object.keys(newChartData).length === 0 || Object.values(newChartData).some(netMap => (
          Object.values(netMap).some(hr => isEmptyOrZero(hr))
        ));
        if (needsFallback) {
          try {
            // Only compute from cached artifacts; do not trigger network on the cache path
            const summaryJson = getCachedData('/submission_summary.json');
            const pricesJson = getCachedData('/submit2prices.json');
            const feedCfgJson = getCachedData('/feed_config.json');
            if (summaryJson && pricesJson && feedCfgJson) {
              const entities = ['UYS', 'ENV'];
              const networks = ['flare', 'songbird'];
              const computeHitRate = (entity, network, tfKey) => {
                try {
                  const sumEN = summaryJson?.[entity]?.[network] || {};
                  const latest = Number(sumEN.latest_voting_round || 0);
                  const start = Number(sumEN[`${tfKey}_last_voting_round`] || 0);
                  if (!latest || !start) return undefined;
                  const cfg = (feedCfgJson?.[entity]?.[network] || []).filter(f => f?.submit === true);
                  const expected = cfg.map(f => String(f.feed_name || '').trim().toLowerCase()).filter(Boolean);
                  if (!expected.length) return undefined;
                  let totalExp = 0; let totalRes = 0;
                  for (let vr = start + 1; vr <= latest; vr++) {
                    const list = pricesJson?.[entity]?.[network]?.[String(vr)] || [];
                    const map = {};
                    if (Array.isArray(list)) {
                      list.forEach(it => {
                        const k = String(it?.feed || '').trim().toLowerCase();
                        if (k) map[k] = Number(it?.result || 0);
                      });
                    }
                    expected.forEach(k => { totalExp += 1; totalRes += Number(map[k] || 0); });
                  }
                  return totalExp > 0 ? Math.round((totalRes / totalExp) * 10000) / 100 : undefined;
                } catch { return undefined; }
              };
              entities.forEach(entity => {
                if (!newChartData[entity]) newChartData[entity] = {};
                networks.forEach(network => {
                  if (!newChartData[entity][network]) newChartData[entity][network] = {};
                  ['15min', '2hr', '6hr'].forEach(tf => {
                    if (!newChartData[entity][network][tf]) {
                      const hr = computeHitRate(entity, network, tf);
                      if (typeof hr === 'number' && hr > 0) {
                        newChartData[entity][network][tf] = { hitRate: hr };
                      }
                    }
                  });
                });
              });
            }
          } catch {}
        }

        // If VR advanced but hit_rates stayed the same, recompute using cached artifacts and override
        try {
          const summaryJson2 = getCachedData('/submission_summary.json');
          const pricesJson2 = getCachedData('/submit2prices.json');
          const feedCfgJson2 = getCachedData('/feed_config.json');
          if (summaryJson2 && pricesJson2 && feedCfgJson2) {
            const entities = Object.keys(newGeneralInfo);
            entities.forEach(entity => {
              Object.keys(newGeneralInfo[entity] || {}).forEach(network => {
                const latestNow = Number(newGeneralInfo[entity]?.[network]?.latest_processed_voting_round || 0);
                const latestPrev = Number(generalInfoData?.[entity]?.[network]?.latest_processed_voting_round || 0);
                const chartNow = newChartData[entity]?.[network] || {};
                const chartPrev = chartData?.[entity]?.[network] || {};
                const unchanged = ['15min','2hr','6hr'].every(tf => {
                  const nowV = chartNow?.[tf]?.hitRate;
                  const prevV = chartPrev?.[tf]?.hitRate;
                  return typeof nowV === 'number' && typeof prevV === 'number' && Math.abs(nowV - prevV) < 0.0001;
                });
                if (latestNow > latestPrev && unchanged) {
                  const compute = (tfKey) => {
                    try {
                      const sumEN = summaryJson2?.[entity]?.[network] || {};
                      const latest = Number(sumEN.latest_voting_round || 0);
                      const start = Number(sumEN[`${tfKey}_last_voting_round`] || 0);
                      if (!latest || !start) return undefined;
                      const cfg = (feedCfgJson2?.[entity]?.[network] || []).filter(f => f?.submit === true);
                      const expected = cfg.map(f => String(f.feed_name || '').trim().toLowerCase()).filter(Boolean);
                      if (!expected.length) return undefined;
                      let totalExp = 0; let totalRes = 0;
                      for (let vr = start + 1; vr <= latest; vr++) {
                        const list = pricesJson2?.[entity]?.[network]?.[String(vr)] || [];
                        const map = {};
                        if (Array.isArray(list)) {
                          list.forEach(it => { const k = String(it?.feed || '').trim().toLowerCase(); if (k) map[k] = Number(it?.result || 0); });
                        }
                        expected.forEach(k => { totalExp += 1; totalRes += Number(map[k] || 0); });
                      }
                      return totalExp > 0 ? Math.round((totalRes / totalExp) * 10000) / 100 : undefined;
                    } catch { return undefined; }
                  };
                  ['15min','2hr','6hr'].forEach(tf => {
                    const hr = compute(tf);
                    if (typeof hr === 'number') {
                      if (!newChartData[entity]) newChartData[entity] = {};
                      if (!newChartData[entity][network]) newChartData[entity][network] = {};
                      newChartData[entity][network][tf] = { hitRate: hr };
                    }
                  });
                }
              });
            });
          }
        } catch {}
        // Do not consume results.json anymore; rely on submission_summary and server gating

        // Merge with previous to avoid transient blanks
        const mergedChart = JSON.parse(JSON.stringify(prevChartDataRef.current || {}));
        Object.keys(newChartData).forEach(entity => {
          mergedChart[entity] = mergedChart[entity] || {};
          Object.keys(newChartData[entity]).forEach(network => {
            mergedChart[entity][network] = mergedChart[entity][network] || {};
            ['15min','2hr','6hr'].forEach(tf => {
              const next = newChartData[entity][network]?.[tf]?.hitRate;
              if (typeof next === 'number' && next > 0) {
                mergedChart[entity][network][tf] = { hitRate: next };
              } else if (mergedChart[entity][network][tf] && typeof mergedChart[entity][network][tf].hitRate === 'number') {
                // keep previous
              } else {
                // leave undefined → placeholder renders "Data N/A"
              }
            });
          });
        });
        setChartData(mergedChart);
        try { localStorage.setItem(LS_CHART, JSON.stringify(mergedChart)); } catch {}
        prevChartDataRef.current = mergedChart;
        setVotePowerData(newVotePowerData);
        try { localStorage.setItem(LS_VP, JSON.stringify(newVotePowerData)); } catch {}
        setVoterRegistration(newVoterRegistration);
        try { localStorage.setItem(LS_REG, JSON.stringify(newVoterRegistration)); } catch {}
        setGeneralInfoData(newGeneralInfo);
        try { localStorage.setItem(LS_GENERAL, JSON.stringify(newGeneralInfo)); } catch {}
        if (!cacheManager.isRefreshing()) {
          updatePageStatus('ftsoSummary', 'green');
        }
        setLoading(false);
        return;
      }
    } catch {}

    // Cold cache fallback — fetch dashboard only
    const timestamp = Date.now();
    return fetch(`${FTSO_PERFORMANCE_ENDPOINT}`)
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} for ${FTSO_PERFORMANCE_ENDPOINT}`);
      }
      return response.json();
    })
    .then(async (dashboardData) => {
      console.log('FTSOSummary: Received FTSO Performance Dashboard data');
      const newChartData = {};
      const newVotePowerData = {};
      const newVoterRegistration = {};
      const newGeneralInfo = {};

      Object.keys(dashboardData).forEach(entity => {
        if (entity === "metadata") return;
        newChartData[entity] = {};
        newVotePowerData[entity] = {};
        newVoterRegistration[entity] = {};
        newGeneralInfo[entity] = {};
        Object.keys(dashboardData[entity]).forEach(network => {
          newChartData[entity][network] = dashboardData[entity][network]?.detailed_metrics?.hit_rates || {};
          newVotePowerData[entity][network] = dashboardData[entity][network]?.detailed_metrics?.vote_power || {};
          newVoterRegistration[entity][network] = dashboardData[entity][network]?.detailed_metrics?.voter_registration || {};
          newGeneralInfo[entity][network] = dashboardData[entity][network]?.general || {};
        });
      });

      // Fallback: if hit_rates missing/empty, compute from artifacts client-side
      const needsFallback = Object.keys(newChartData).length === 0 || Object.values(newChartData).some(netMap => (
        Object.values(netMap).some(hr => !hr || Object.keys(hr).length === 0)
      ));

      if (needsFallback) {
        try {
          const [summaryResp, pricesResp, feedCfgResp] = await Promise.all([
            fetch(`${SUBMISSION_SUMMARY_ENDPOINT}`),
            fetch(`${SUBMIT2_PRICES_ENDPOINT}`),
            fetch(`${FEED_CONFIG_ENDPOINT}`),
          ]);
          const [summaryJson, pricesJson, feedCfgJson] = await Promise.all([
            summaryResp.ok ? summaryResp.json() : {},
            pricesResp.ok ? pricesResp.json() : {},
            feedCfgResp.ok ? feedCfgResp.json() : {},
          ]);
          const entities = ['UYS', 'ENV'];
          const networks = ['flare', 'songbird'];
          const computeHitRate = (entity, network, tfKey) => {
            try {
              const sumEN = summaryJson?.[entity]?.[network] || {};
              const latest = Number(sumEN.latest_voting_round || 0);
              const start = Number(sumEN[`${tfKey}_last_voting_round`] || 0);
              if (!latest || !start) return undefined;
              const cfg = (feedCfgJson?.[entity]?.[network] || []).filter(f => f?.submit === true);
              const expected = cfg.map(f => String(f.feed_name || '').trim().toLowerCase()).filter(Boolean);
              if (!expected.length) return undefined;
              const roundsObj = pricesJson?.[entity]?.[network] || {};
              const roundKeys = Object.keys(roundsObj)
                .map(r => parseInt(r, 10))
                .filter(n => Number.isFinite(n) && n > start && n <= latest)
                .sort((a, b) => a - b);
              if (roundKeys.length === 0) return undefined;
              let totalExp = 0; let totalRes = 0;
              for (const vr of roundKeys) {
                const list = roundsObj[String(vr)] || [];
                const map = {};
                if (Array.isArray(list)) {
                  list.forEach(it => {
                    const k = String(it?.feed || '').trim().toLowerCase();
                    if (k) map[k] = Number(it?.result || 0);
                  });
                }
                expected.forEach(k => { totalExp += 1; totalRes += Number(map[k] || 0); });
              }
              return totalExp > 0 ? Math.round((totalRes / totalExp) * 10000) / 100 : undefined;
            } catch { return undefined; }
          };
          entities.forEach(entity => {
            if (!newChartData[entity]) newChartData[entity] = {};
            networks.forEach(network => {
              if (!newChartData[entity][network]) newChartData[entity][network] = {};
              ['15min', '2hr', '6hr'].forEach(tf => {
                if (!newChartData[entity][network][tf]) {
                  const hr = computeHitRate(entity, network, tf);
                  if (typeof hr === 'number') {
                    newChartData[entity][network][tf] = { hitRate: hr };
                  }
                }
              });
            });
          });
        } catch (e) {
          console.warn('FTSOSummary: Fallback hit-rate computation failed', e);
        }
      }
      
      // No results.json fetch; infer latest rounds via submission_summary and dashboard general info

      // Compute latest summary VR map from fetched submission_summary
      try {
        const sumResp = await fetch(`${SUBMISSION_SUMMARY_ENDPOINT}`);
        const sumJson = sumResp.ok ? await sumResp.json() : {};
        if (sumJson && typeof sumJson === 'object') {
          const vrMap = {};
          Object.keys(sumJson || {}).forEach(entity => {
            if (entity === 'metadata') return;
            vrMap[entity] = vrMap[entity] || {};
            Object.keys(sumJson[entity] || {}).forEach(network => {
              vrMap[entity][network] = Number(sumJson[entity][network]?.latest_voting_round || 0);
            });
          });
          setLatestSummaryVR(vrMap);
        }
      } catch {}

      // Merge with previous to avoid transient blanks
      const mergedChart = JSON.parse(JSON.stringify(prevChartDataRef.current || {}));
      Object.keys(newChartData).forEach(entity => {
        mergedChart[entity] = mergedChart[entity] || {};
        Object.keys(newChartData[entity]).forEach(network => {
          mergedChart[entity][network] = mergedChart[entity][network] || {};
          ['15min','2hr','6hr'].forEach(tf => {
            const next = newChartData[entity][network]?.[tf]?.hitRate;
            if (typeof next === 'number') {
              mergedChart[entity][network][tf] = { hitRate: next };
            }
          });
        });
      });
      setChartData(mergedChart);
      try { localStorage.setItem(LS_CHART, JSON.stringify(mergedChart)); } catch {}
      prevChartDataRef.current = mergedChart;
      setVotePowerData(newVotePowerData);
      try { localStorage.setItem(LS_VP, JSON.stringify(newVotePowerData)); } catch {}
      setVoterRegistration(newVoterRegistration);
      try { localStorage.setItem(LS_REG, JSON.stringify(newVoterRegistration)); } catch {}
      setGeneralInfoData(newGeneralInfo);
      try { localStorage.setItem(LS_GENERAL, JSON.stringify(newGeneralInfo)); } catch {}
      if (!cacheManager.isRefreshing()) {
        updatePageStatus('ftsoSummary', 'green');
      }

    })
    .catch(error => {
      console.error('Error fetching FTSO Performance Dashboard data for FTSOSummary:', error);
      setError(error.message);
      if (!cacheManager.isRefreshing()) {
        updatePageStatus('ftsoSummary', 'red');
      }
    })
    .finally(() => {
      setLoading(false);
    });
  }, [updatePageStatus, FTSO_PERFORMANCE_ENDPOINT]);

  // On SSE, only refetch when ftso_performance_dashboard.json actually changed,
  // using export_timestamps.json as the source of truth for last_updated
  const lastDashTsRef = useRef({ dash: null, summary: null, prices: null, feedCfg: null });
  const checkAndFetchOnSse = useCallback(async () => {
    try {
      const tsResp = await fetch(`${API}/export_timestamps.json`);
      if (!tsResp.ok) return;
      const tsJson = await tsResp.json();
      const next = {
        dash: tsJson?.['ftso_performance_dashboard.json']?.last_updated || null,
        summary: tsJson?.['submission_summary.json']?.last_updated || null,
        prices: tsJson?.['submit2prices.json']?.last_updated || null,
        feedCfg: tsJson?.['feed_config.json']?.last_updated || null,
      };
      const changed = Object.keys(next).some(k => next[k] && next[k] !== lastDashTsRef.current[k]);
      if (changed) {
        lastDashTsRef.current = next;
        // Give the prefetcher a brief head start to populate cache coherently, then update
        await new Promise(r => setTimeout(r, 200));
        await fetchData();
      }
    } catch {}
  }, [API, fetchData]);

  useDataUpdates(checkAndFetchOnSse);

  useEffect(() => {
    let newEntityNetworkList = [];

    if (displayOrder && Array.isArray(displayOrder)) {
      newEntityNetworkList = displayOrder.map(item => ({ entity: item.entity, network: item.network }));
    } else {
      const entities = new Set();
      const sourceForChartEntities = chartData;
      
      Object.keys(sourceForChartEntities).forEach(entity => entities.add(entity));
      Object.keys(votePowerData).forEach(entity => entities.add(entity));
      Object.keys(voterRegistration).forEach(entity => entities.add(entity));
      Object.keys(generalInfoData).forEach(entity => entities.add(entity));

      entities.forEach(entity => {
        const networks = new Set();
        if (sourceForChartEntities[entity]) Object.keys(sourceForChartEntities[entity]).forEach(net => networks.add(net));
        if (votePowerData[entity]) Object.keys(votePowerData[entity]).forEach(net => networks.add(net));
        if (voterRegistration[entity]) Object.keys(voterRegistration[entity]).forEach(net => networks.add(net));
        if (generalInfoData[entity]) Object.keys(generalInfoData[entity]).forEach(net => networks.add(net));
        
        networks.forEach(network => {
          newEntityNetworkList.push({ entity, network });
        });
      });
    }
    setEntityNetworkList(newEntityNetworkList);
  }, [chartData, votePowerData, voterRegistration, generalInfoData, displayOrder]);

  useEffect(() => {
    console.log('FTSO Summary component mounted/props changed, performing data sync');

    fetchData()
      .finally(() => {
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
          console.log('FTSOSummary initial load process complete, isInitialLoadRef set to false.');
        }
      });
    
    return () => {
      console.log('FTSO Summary component unmounting');
      setChartData({});
      setVotePowerData({});
      setVoterRegistration({});
      setGeneralInfoData({});
      setError(null);
      isInitialLoadRef.current = true;
    };
  }, [fetchData]);

  if (loading && isInitialLoadRef.current) {
    return <LoadingAnimation />;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  return (
    <div className="ftso-summary-wrapper" ref={internalScrollerRef}>
      <div className="ftso-summary">
        {entityNetworkList.map(({ entity, network }) => {
          // Prefer gated latest VR computed by backend (aligned with HR calculations)
          const gatedFromServer = Number(generalInfoData?.[entity]?.[network]?.gated_latest_voting_round || 0);
          if (gatedFromServer > 0) {
            // Simple path when backend provides gated VR
          }
          // Fallback to previous client-side gating when server field missing
          const latestVR = Number(generalInfoData?.[entity]?.[network]?.latest_processed_voting_round || 0);
          let fallbackVR = 0;
          try {
            const sumCache = getCachedData('/submission_summary.json');
            fallbackVR = Number(sumCache?.[entity]?.[network]?.latest_voting_round || 0);
          } catch {}
          const summaryLatestForPair = Number(latestSummaryVR?.[entity]?.[network] || 0);
          const gatedLatest = gatedFromServer > 0
            ? gatedFromServer
            : (latestVR && summaryLatestForPair ? Math.min(latestVR, summaryLatestForPair) : (latestVR || summaryLatestForPair || 0));
          const latestRoundForDisplay = gatedLatest || summaryLatestForPair || fallbackVR || 'N/A';
          return (
            <div key={`${entity}-${network}`} className="network-section">
              <div className="header-section">
                <div className="title-group">
                  <div className="entity-name">{entity}</div>
                  <div className="network-name">
                    {network === 'flare' ? 'Flare' : 'Songbird'}
                    <span className="ftso-summary-latest-round">
                      &nbsp;({latestRoundForDisplay})
                    </span>
                  </div>
                </div>
                {voterRegistration?.[entity]?.[network] && Object.keys(voterRegistration[entity][network]).length > 0 ? (
                  <div className="registration-status">
                    <div className="registration-line">
                      <div 
                        className={`status-dot ${voterRegistration[entity][network].registered_current_epoch ? 'bg-green' : 'bg-red'}`} 
                        title={`Current Epoch Registration: ${voterRegistration[entity][network].registered_current_epoch ? 'Registered' : 'Not Registered'}`}
                      />
                      <span className="epoch-text">
                        {voterRegistration[entity][network].registered_current_epoch ? 'Registered' : 'Not Registered'} (Epoch {voterRegistration[entity][network].current_epoch})
                      </span>
                    </div>
                    <div className="registration-line">
                      <div 
                        className={`status-dot ${voterRegistration[entity][network].preregistered_next_epoch ? 'bg-green' : 'bg-red'}`} 
                        title={`Next Epoch Pre-Registration: ${voterRegistration[entity][network].preregistered_next_epoch ? 'Pre-Registered' : 'Not Pre-Registered'}`}
                      />
                      <span className="epoch-text">
                        {voterRegistration[entity][network].preregistered_next_epoch ? 'Pre-Registered' : 'Not Pre-Registered'} (Epoch {voterRegistration[entity][network].next_epoch})
                      </span>
                    </div>
                  </div>
                ) : loading ? (
                  <div className="registration-status-loading">Loading registration status...</div>
                ) : null}
              </div>
    
              {chartData?.[entity]?.[network] && Object.keys(chartData[entity][network]).length > 0 ? (
                <div className="charts-container">
                  {['15min', '2hr', '6hr'].map((timeframe) => (
                    chartData[entity]?.[network]?.[timeframe] && typeof chartData[entity][network][timeframe].hitRate === 'number' ? (
                      <div key={timeframe} className="gauge-chart-wrapper">
                        <div className="timeframe-label">
                          {timeframe === '15min' ? '15 Minutes' :
                           timeframe === '2hr' ? '2 Hours' :
                           '6 Hours'}
                        </div>
                        <SmoothGauge
                          id={`gauge-${entity}-${network}-${timeframe}`}
                          value={chartData[entity][network][timeframe].hitRate}
                        />
                        <div className="hit-rate-text">
                          {`${chartData[entity][network][timeframe].hitRate.toFixed(2)}%`}
                        </div>
                      </div>
                    ) : (
                      <div key={timeframe} className="gauge-chart-wrapper placeholder">
                        <div className="timeframe-label">
                          {timeframe === '15min' ? '15 Minutes' :
                           timeframe === '2hr' ? '2 Hours' :
                           '6 Hours'}
                        </div>
                        <div className="chart-placeholder">Data N/A</div>
                      </div>
                    )
                  ))}
                </div>
              ) : loading ? (
                <div className="charts-container-loading">Loading charts...</div>
              ) : null}
              
              {votePowerData?.[entity]?.[network] && typeof votePowerData[entity][network].current_vp === 'string' ? (
                <div className="vote-power-info">
                  <div className="vp-header">Current Vote Power</div>
                  <div className="vp-details">
                    <div className="current-vp">
                      {Math.round(parseFloat(votePowerData[entity][network].current_vp || "0")).toLocaleString()}
                    </div>
                    <div className={`vp-change ${parseFloat(votePowerData[entity][network].current_vp || "0") >= parseFloat(votePowerData[entity][network].previous_vp || "0") ? 'positive' : 'negative'}`}>
                      {parseFloat(votePowerData[entity][network].current_vp || "0") >= parseFloat(votePowerData[entity][network].previous_vp || "0")
                        ? <GrowthIcon /> 
                        : <DecreaseIcon />}
                    </div>
                  </div>
                </div>
              ) : loading ? (
                <div className="vote-power-loading">Loading vote power...</div>
              ) : null}
            </div>
          );
        })}
        {entityNetworkList.length === 0 && !loading && !error && (
          <div className="no-data-message">No FTSO summary data available for any entity or network.</div>
        )}
      </div>
    </div>
  );
}

export default React.memo(FTSOSummary);
